import { apiClient } from '@/shared/services/http/http.service'
import { SURVEY_ENDPOINTS } from '../constants/endpoints.constant.js'

const TEXT_TYPES = new Set(['text', 'comment', 'multipletext'])
const MATRIX_TYPES = new Set(['matrix', 'matrixdropdown', 'matrixdynamic'])
const ATTACHMENT_TYPES = new Set(['file'])

function buildFilterParameters(filters = {}) {
  const parameters = {}
  if (filters.businessUnit)
    parameters.businessUnit = filters.businessUnit
  if (filters.site)
    parameters.site = filters.site
  if (filters.area)
    parameters.area = filters.area
  return parameters
}

function mapMetrics(metrics = {}) {
  return {
    totalRespondents: metrics.totalRespondents ?? 0,
    averageResponseTimeSeconds: metrics.averageResponseTime ?? null,
    surveyDurationSeconds: metrics.surveyDuration ?? null,
  }
}

function mapTextQuestion(q, aggregation) {
  return {
    name: q.name,
    title: q.title,
    type: q.type,
    responseCount: aggregation.length,
    data: { sample: aggregation },
  }
}

function mapAttachmentQuestion(q, aggregation) {
  const items = Array.isArray(aggregation) ? aggregation : []
  return {
    name: q.name,
    title: q.title,
    type: q.type,
    responseCount: items.length,
    data: {
      items,
      fileCount: items.reduce((sum, item) => sum + (item.files?.length ?? 0), 0),
    },
  }
}

function mapRatingQuestion(q, aggregation) {
  const distribution = Object.entries(aggregation)
    .map(([value, count]) => ({ value: Number(value), count }))
    .sort((a, b) => a.value - b.value)

  const total = distribution.reduce((sum, d) => sum + d.count, 0)
  const average = total > 0
    ? distribution.reduce((sum, d) => sum + d.value * d.count, 0) / total
    : null

  return {
    name: q.name,
    title: q.title,
    type: q.type,
    responseCount: total,
    data: { distribution, average },
  }
}

function mapMatrixQuestion(q, aggregation) {
  const total = Object.values(aggregation).reduce((a, b) => a + b, 0)
  return {
    name: q.name,
    title: q.title,
    type: q.type,
    responseCount: total,
    data: { aggregation },
  }
}

const STRUCTURE_QUESTION_NAMES = new Set(['businessUnit', 'site', 'area'])

function buildIdToNameMap(availableFilters = {}) {
  const map = {}
  for (const items of Object.values(availableFilters)) {
    for (const item of items) {
      if (item.id && item.name)
        map[item.id] = item.name
    }
  }
  return map
}

function resolveAggregationLabels(aggregation, idToName) {
  return Object.fromEntries(
    Object.entries(aggregation).map(([key, count]) => [idToName[key] ?? key, count]),
  )
}

function mapChoiceQuestion(q, aggregation) {
  const total = Object.values(aggregation).reduce((a, b) => a + b, 0)
  const choices = Object.entries(aggregation).map(([label, count]) => ({
    value: label,
    label,
    count,
    percent: total > 0 ? (count / total) * 100 : 0,
  }))
  return {
    name: q.name,
    title: q.title,
    type: q.type,
    responseCount: total,
    data: { choices },
  }
}

function mapQuestion(q, idToName) {
  if (ATTACHMENT_TYPES.has(q.type))
    return mapAttachmentQuestion(q, q.aggregation)

  const isTextType = TEXT_TYPES.has(q.type)
  let aggregation = q.aggregation ?? (isTextType ? [] : {})

  if (!isTextType && STRUCTURE_QUESTION_NAMES.has(q.name) && typeof aggregation === 'object' && !Array.isArray(aggregation)) {
    aggregation = resolveAggregationLabels(aggregation, idToName)
  }

  if (isTextType)
    return mapTextQuestion(q, aggregation)
  if (q.type === 'rating')
    return mapRatingQuestion(q, aggregation)
  if (MATRIX_TYPES.has(q.type))
    return mapMatrixQuestion(q, aggregation)
  return mapChoiceQuestion(q, aggregation)
}

function mapReport(data = {}) {
  const availableFilters = data.availableFilters ?? { businessUnit: [], site: [], area: [] }
  const idToName = buildIdToNameMap(availableFilters)
  return {
    metrics: mapMetrics(data.metrics ?? {}),
    questions: (data.questions ?? []).map(q => mapQuestion(q, idToName)),
    availableFilters,
    respondents: data.respondents ?? [],
  }
}

export async function getSurveyReport(surveyId, filters = {}) {
  const url = SURVEY_ENDPOINTS.REPORT.replace(':id', surveyId)
  const parameters = buildFilterParameters(filters)
  const response = await apiClient('generic-survey').get(url, { params: parameters })
  return mapReport(response.data?.data ?? response.data)
}
